import { useEffect, useState } from "react";
import { Menu, X, MapPin } from "lucide-react";
import { openWhatsapp, openAgendar } from "@/lib/contact";

const NAV = [
    { label: "Servicios", href: "#servicios" },
    { label: "Nosotros", href: "#nosotros" },
    { label: "Por qué elegirnos", href: "#diferenciales" },
    { label: "Contacto", href: "#contacto" },
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (href) => {
        setOpen(false);
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <header
            data-testid="site-header"
            className={`sticky top-0 z-40 backdrop-blur-xl transition-all duration-300 ${
                scrolled
                    ? "bg-[var(--uma-cream)]/85 border-b border-[var(--uma-forest)]/10 shadow-[0_1px_0_rgba(10,38,20,0.04)]"
                    : "bg-[var(--uma-cream)]/60 border-b border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-5 md:px-10 h-[68px] md:h-[80px] flex items-center justify-between">
                <a
                    href="#top"
                    data-testid="brand-logo"
                    onClick={(e) => {
                        e.preventDefault();
                        window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className="group flex items-center gap-3"
                >
                    <img
                        src="/logo-altez.png"
                        alt="Altez Palarin Estudio Contable"
                        className="h-12 md:h-14 w-auto object-contain"
                        style={{ objectPosition: "center top" }}
                    />
                    <div className="leading-none hidden sm:block">
                        <div className="font-serif-display text-[20px] md:text-[22px] tracking-tight text-[var(--uma-forest)]">
                            Altez Palarin
                        </div>
                        <div className="text-[9px] uppercase tracking-[0.28em] text-[var(--uma-text-soft)] mt-1">
                            Estudio Contable
                        </div>
                    </div>
                </a>

                <nav
                    data-testid="primary-nav"
                    className="hidden lg:flex items-center gap-8"
                >
                    {NAV.map((n) => (
                        <button
                            key={n.href}
                            onClick={() => go(n.href)}
                            data-testid={`nav-${n.href.slice(1)}`}
                            className="text-[14px] text-[var(--uma-text)] hover:text-[var(--uma-forest)] transition-colors font-medium relative group"
                        >
                            {n.label}
                            <span className="absolute -bottom-1 left-0 w-0 h-px bg-[var(--uma-gold)] group-hover:w-full transition-all duration-300" />
                        </button>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <div className="flex items-center gap-1.5 text-[12px] text-[var(--uma-text-soft)] mr-2">
                        <MapPin className="h-3.5 w-3.5 text-[var(--uma-gold)]" />
                        Rocha · Castillos
                    </div>
                    <button
                        data-testid="header-cta-whatsapp"
                        onClick={() => openWhatsapp()}
                        className="text-[13px] font-medium text-[var(--uma-forest)] hover:text-[var(--uma-gold)] transition-colors"
                    >
                        WhatsApp
                    </button>
                    <button
                        data-testid="header-cta-primary"
                        onClick={() => openAgendar()}
                        className="group inline-flex items-center gap-2 bg-[var(--uma-gold-cta)] text-[var(--uma-forest)] hover:bg-[var(--uma-gold-cta-hover)] px-5 py-2.5 rounded-md text-[13px] font-medium transition-all"
                    >
                        Consulta gratis
                    </button>
                </div>

                <button
                    data-testid="mobile-menu-toggle"
                    aria-label="Abrir menú"
                    onClick={() => setOpen((v) => !v)}
                    className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg border border-[var(--uma-forest)]/15 text-[var(--uma-forest)]"
                >
                    {open ? (
                        <X className="h-5 w-5" />
                    ) : (
                        <Menu className="h-5 w-5" />
                    )}
                </button>
            </div>

            {open && (
                <div
                    data-testid="mobile-menu"
                    className="lg:hidden border-t border-[var(--uma-forest)]/10 bg-[var(--uma-cream)]"
                >
                    <div className="px-5 py-5 flex flex-col gap-1">
                        {NAV.map((n) => (
                            <button
                                key={n.href}
                                onClick={() => go(n.href)}
                                data-testid={`mobile-nav-${n.href.slice(1)}`}
                                className="text-left py-3 text-[15px] font-medium text-[var(--uma-forest)] border-b border-[var(--uma-forest)]/5"
                            >
                                {n.label}
                            </button>
                        ))}
                        <div className="grid grid-cols-2 gap-3 pt-4">
                            <button
                                data-testid="mobile-cta-whatsapp"
                                onClick={() => {
                                    setOpen(false);
                                    openWhatsapp();
                                }}
                                className="py-3 rounded-full border border-[var(--uma-forest)] text-[var(--uma-forest)] text-sm font-medium"
                            >
                                WhatsApp
                            </button>
                            <button
                                data-testid="mobile-cta-primary"
                                onClick={() => {
                                    setOpen(false);
                                    openAgendar();
                                }}
                                className="py-3 rounded-full bg-[var(--uma-gold-cta)] text-[var(--uma-forest)] text-sm font-medium"
                            >
                                Agendar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
